"use client";

/**
 * Попап «Подобрать автомобиль» — открывается по клику «Забронировать»/фото
 * машины на ГЛАВНОЙ в статике (город и даты ещё не заданы, машины не подобраны
 * под период). По образцу idle-попапа Солнышко, но триггер — клик, а не таймер.
 * Логика: выбрал город + даты → редирект на страницу города
 * /{slug}?start&end&start_time&end_time. Место подачи там берётся по умолчанию
 * (первое по индексу — эффект в SearchBar), а даты подставляются и сразу
 * запускают поиск свободных машин (SearchBar читает эти query-параметры).
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import RangeDateTimePicker from "./RangeDateTimePicker";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";
import type { ApiCity } from "@/lib/api";

function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtISO(s: string) {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}
// Лид-тайм: старт «сегодня» → минимальное время = сейчас + 2 ч, округлённое
// вверх до 15 минут; за полночь — 23:45 (как в SearchBar).
function leadFloorTime(): string {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  if (d.getDate() !== new Date().getDate()) return "23:45";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={`size-5 shrink-0 text-text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
      <path d="M6 9.5L12 15.5L18 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function BookingPromptModal({
  cities, initialStation, onClose,
}: {
  cities: ApiCity[];
  initialStation?: string;     // город кликнутой машины — предвыбираем его
  onClose: () => void;
}) {
  const router = useRouter();
  const { locale } = useLocale();
  const isEn = locale === "en";
  const [mounted, setMounted] = useState(false);
  const [station, setStation] = useState<string>(
    () => cities.find((c) => c.station === initialStation)?.station || "",
  );
  const [cityOpen, setCityOpen] = useState(false);
  const [startISO, setStartISO] = useState("");
  const [endISO, setEndISO] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [err, setErr] = useState("");

  const city = cities.find((c) => c.station === station) || null;
  const ready = !!city && !!startISO && !!endISO; // CTA активна только когда выбраны и город, и даты

  // Портал монтируем только на клиенте; блокируем скролл фона, закрытие по Esc.
  useEffect(() => {
    setMounted(true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const cardRef = useRef<HTMLDivElement>(null);

  const submit = () => {
    if (!ready || !city) return; // подстраховка: кнопка и так disabled до выбора
    const st = startTime || "12:00";
    const et = endTime || "12:00";
    const q = new URLSearchParams({ start: startISO, end: endISO, start_time: st, end_time: et });
    track("booking_prompt_submit", { city: city.slug });
    router.push(`/${city.slug}?${q.toString()}`);
    onClose();
  };

  if (!mounted) return null;

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[2100] flex items-center justify-center bg-[rgba(15,18,26,0.55)] p-4 backdrop-blur-[6px]"
    >
      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[calc(100vh-32px)] w-full max-w-[640px] overflow-y-auto rounded-2xl bg-white p-5 shadow-[0_12px_48px_rgba(0,0,0,0.28)] sm:p-6"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={isEn ? "Close" : "Закрыть"}
          className="absolute right-4 top-4 flex size-9 cursor-pointer items-center justify-center rounded-full text-text-muted transition hover:bg-bg"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>

        <h2 className="pr-8 text-[22px] font-semibold leading-tight text-text">{isEn ? "Find a car" : "Подобрать автомобиль"}</h2>
        <p className="mt-1.5 text-[14px] leading-5 text-text-muted">
          {isEn ? "Select a city and dates — we'll show cars available for that period." : "Выберите город и даты — покажем свободные машины на этот период."}
        </p>

        {/* Город подачи */}
        <label className="mt-5 mb-1.5 block text-[13px] font-medium text-text-muted">{isEn ? "Pickup city" : "Город подачи"}</label>
        <div className="relative">
          <button
            type="button"
            onClick={() => { setCityOpen((o) => !o); setErr(""); }}
            className={`flex h-[52px] w-full cursor-pointer items-center justify-between rounded-xl border px-4 text-left transition ${cityOpen ? "border-primary bg-white" : "border-[#C9D3E6] bg-bg hover:bg-white"}`}
          >
            <span className={`truncate text-[16px] ${city ? "text-text" : "text-text-muted"}`}>{city ? (isEn && city.name_en ? city.name_en : city.name) : (isEn ? "Select a city" : "Выберите город")}</span>
            <ChevronIcon open={cityOpen} />
          </button>
          {cityOpen && (
            <ul className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-[260px] overflow-y-auto rounded-xl bg-white py-1 shadow-[0_12px_40px_rgba(20,29,69,0.2)] ring-1 ring-border">
              {cities.map((c) => (
                <li key={c.station}>
                  <button
                    type="button"
                    onClick={() => { setStation(c.station); setCityOpen(false); setErr(""); }}
                    className={`block w-full cursor-pointer px-4 py-2.5 text-left text-[15px] transition hover:bg-bg ${c.station === station ? "font-medium text-primary" : "text-text"}`}
                  >
                    {isEn && c.name_en ? c.name_en : c.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Даты аренды — инлайн-календарь; «Готово» фиксирует даты в state модалки */}
        <label className="mt-5 mb-1.5 block text-[13px] font-medium text-text-muted">
          {isEn ? "Rental dates" : "Даты аренды"}{startISO && endISO ? `: ${fmtISO(startISO)} ${startTime || ""} → ${fmtISO(endISO)} ${endTime || ""}` : ""}
        </label>
        <div className="rounded-xl bg-bg">
          <RangeDateTimePicker
            startISO={startISO}
            endISO={endISO}
            startTime={startTime}
            endTime={endTime}
            minDate={ymd(new Date())}
            minTime={leadFloorTime()}
            minDays={1}
            instant
            onRangeChange={(s, e) => { setStartISO(s); setEndISO(e); setErr(""); }}
            onStartTime={setStartTime}
            onEndTime={setEndTime}
            onClose={() => {}}
          />
        </div>

        {err && <p className="mt-3 text-[13px] text-danger">{err}</p>}

        <button
          type="button"
          onClick={submit}
          disabled={!ready}
          className={`mt-5 w-full rounded-xl py-4 text-[16px] font-semibold leading-6 text-white transition ${ready ? "cursor-pointer bg-primary hover:bg-primary-hover" : "cursor-not-allowed bg-[#A9B4CC]"}`}
        >
          {isEn ? "Show cars" : "Показать автомобили"}
        </button>
      </div>
    </div>,
    document.body,
  );
}
